import { apiClient } from './client';
/**
 * Baz Skills Integration API Client
 * Provides methods for browsing, searching, and managing Baz skills as agents
 */
export const bazApi = {
    // ============================================================================
    // Skills Discovery
    // ============================================================================
    /**
     * List available Baz skills with pagination and filtering
     * @param params - Pagination and filter parameters
     * @returns List of skills with pagination metadata
     */
    listSkills: async (params) => {
        const response = await apiClient.get('/v1/admin/baz/skills', { params });
        return response.data;
    },
    /**
     * Semantic search for skills using natural language queries
     * @param request - Search query and filters
     * @returns Ranked search results with relevance scores
     */
    searchSkills: async (request) => {
        const response = await apiClient.post('/v1/admin/baz/skills/search', request);
        return response.data;
    },
    /**
     * Get detailed information about a specific skill
     * @param skillId - Unique skill identifier
     * @returns Complete skill details including documentation and requirements
     */
    getSkillDetails: async (skillId) => {
        const response = await apiClient.get(`/v1/admin/baz/skills/${skillId}`);
        return response.data;
    },
    // ============================================================================
    // Agent Installation
    // ============================================================================
    /**
     * Install a Baz skill as an agent in Cosmo
     * @param request - Installation configuration (name, model, system prompt, etc.)
     * @returns Installation result with agent ID
     */
    installSkillAsAgent: async (skillId, request) => {
        const response = await apiClient.post(`/v1/admin/baz/skills/${skillId}/install`, request);
        return response.data;
    },
    // ============================================================================
    // Agent Management
    // ============================================================================
    /**
     * List all installed Baz agents
     * @param params - Pagination parameters
     * @returns List of Baz agents with pagination metadata
     */
    listBazAgents: async (params) => {
        const response = await apiClient.get('/v1/admin/baz/agents', { params });
        return response.data;
    },
    /**
     * Get details about a specific Baz agent
     * @param agentId - Baz agent identifier
     * @returns Detailed agent information including usage stats
     */
    getBazAgentDetails: async (agentId) => {
        const response = await apiClient.get(`/v1/admin/baz/agents/${agentId}`);
        return response.data;
    },
    /**
     * Update a Baz agent's configuration
     * @param agentId - Baz agent identifier
     * @param request - Updated configuration
     * @returns Updated agent details
     */
    updateBazAgent: async (agentId, request) => {
        const response = await apiClient.put(`/v1/admin/baz/agents/${agentId}`, request);
        return response.data;
    },
    /**
     * Uninstall a Baz agent
     * @param agentId - Baz agent identifier
     * @returns Success confirmation
     */
    uninstallBazAgent: async (agentId) => {
        const response = await apiClient.delete(`/v1/admin/baz/agents/${agentId}`);
        return response.data;
    },
    /**
     * Check if updates are available for a Baz agent
     * @param agentId - Baz agent identifier
     * @returns Update availability information
     */
    checkForUpdates: async (agentId) => {
        const response = await apiClient.get(`/v1/admin/baz/agents/${agentId}/updates`);
        return response.data;
    },
    // ============================================================================
    // Integration Health
    // ============================================================================
    /**
     * Get Baz integration health status and statistics
     * @returns Connection status, health metrics, and usage statistics
     */
    getIntegrationStatus: async () => {
        const response = await apiClient.get('/v1/admin/baz/status');
        return response.data;
    },
};
